import {
  Button,
  ComboBox,
  ComboBoxStateContext,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
} from 'react-aria-components';
import { Minus, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { datalogClient } from '../services/datalogClient';
import { useWorkspaceStore } from '../store/workspace';
import type {
  ChannelDefinition,
  GraphPanel as GraphPanelType,
  RangeStatistics,
  RenderWindowResult,
} from '../types';
import { useElementSize } from '../lib/useElementSize';
import { sortChannelsByName } from '../lib/channels';
import { GraphIdentityBadge } from './GraphIdentityBadge';
import { TimeSeriesChart, type ChartChannel } from './TimeSeriesChart';

const CLEAR_KEY = '__clear__';

type ChannelComboProps = {
  channels: ChannelDefinition[];
  selectedId: string | null;
  unavailableIds: Set<string>;
  label: string;
  onChange: (channelId: string | null) => void;
};

type ChannelComboItem = {
  id: string;
  selectorName: string;
  shortName: string;
  unit: string;
};

const MOBILE_SELECTOR_QUERY = '(hover: none) and (pointer: coarse)';

function useMobileChannelSelector() {
  const [isMobileSelector, setIsMobileSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(MOBILE_SELECTOR_QUERY).matches,
  );

  useEffect(() => {
    const media = window.matchMedia?.(MOBILE_SELECTOR_QUERY);
    if (!media) return;
    const update = () => setIsMobileSelector(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return isMobileSelector;
}

type ChannelComboOptionsProps = Pick<ChannelComboProps, 'selectedId' | 'unavailableIds'> & {
  onClear: () => void;
};

function ChannelComboOptions({ selectedId, unavailableIds, onClear }: ChannelComboOptionsProps) {
  const comboBoxState = useContext(ComboBoxStateContext);

  return (
    <Popover className="channel-popover" placement="bottom start">
      <ListBox<ChannelComboItem> className="channel-listbox">
        {(item) => (
          <ListBoxItem
            id={item.id}
            textValue={item.selectorName}
            isDisabled={item.id !== selectedId && unavailableIds.has(item.id)}
            onAction={item.id === CLEAR_KEY
              ? () => {
                  onClear();
                  comboBoxState?.close();
                }
              : undefined}
            className={`channel-option${item.id === CLEAR_KEY ? ' channel-option-clear' : ''}`}
          >
            <span>{item.selectorName}</span>
            <small>{item.unit}</small>
          </ListBoxItem>
        )}
      </ListBox>
    </Popover>
  );
}

function ChannelCombo({ channels, selectedId, unavailableIds, label, onChange }: ChannelComboProps) {
  const isMobileSelector = useMobileChannelSelector();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const items = useMemo(
    () => [
      { id: CLEAR_KEY, selectorName: 'Clear slot', shortName: '', unit: '' },
      ...sortChannelsByName(channels).map((channel) => ({
        id: channel.id,
        selectorName: channel.displayName,
        shortName: channel.shortName,
        unit: channel.unit ?? '',
      })),
    ],
    [channels],
  );
  const searchableValues = useMemo(() => {
    const values = new Map<string, string[]>();
    for (const item of items) {
      const entries = values.get(item.selectorName) ?? [];
      entries.push(`${item.selectorName} ${item.shortName} ${item.unit}`.toLocaleLowerCase());
      values.set(item.selectorName, entries);
    }
    return values;
  }, [items]);

  return (
    <ComboBox<ChannelComboItem>
      aria-label={label}
      selectedKey={selectedId}
      defaultItems={items}
      defaultFilter={(textValue, inputValue) => {
        const query = inputValue.trim().toLocaleLowerCase();
        return !query || searchableValues.get(textValue)?.some((value) => value.includes(query)) === true;
      }}
      menuTrigger={isMobileSelector ? 'manual' : 'focus'}
      onSelectionChange={(key) => onChange(key && key !== CLEAR_KEY ? String(key) : null)}
      className="channel-combobox"
    >
      <div className="combo-input-wrap">
        <Input
          placeholder="Select channel"
          readOnly={isMobileSelector}
          inputMode={isMobileSelector ? 'none' : 'search'}
        />
        <Button
          ref={triggerRef}
          aria-label={`Open ${label} choices`}
          onPress={() => {
            if (isMobileSelector) {
              requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
            }
          }}
        />
      </div>
      <ChannelComboOptions
        selectedId={selectedId}
        unavailableIds={unavailableIds}
        onClear={() => onChange(null)}
      />
    </ComboBox>
  );
}

type GraphPanelProps = {
  graph: GraphPanelType;
  channels: ChannelDefinition[];
  onNotice: (message: string) => void;
};

export function GraphPanel({ graph, channels, onNotice }: GraphPanelProps) {
  const session = useWorkspaceStore((state) => state.session)!;
  const visibleTimeRange = useWorkspaceStore((state) => state.visibleTimeRange);
  const cursorTime = useWorkspaceStore((state) => state.cursorTime);
  const focusedGraphId = useWorkspaceStore((state) => state.focusedGraphId);
  const assignChannel = useWorkspaceStore((state) => state.assignChannel);
  const setAutoScale = useWorkspaceStore((state) => state.setAutoScale);
  const setVisibleTimeRange = useWorkspaceStore((state) => state.setVisibleTimeRange);
  const setCursorTime = useWorkspaceStore((state) => state.setCursorTime);
  const removeGraph = useWorkspaceStore((state) => state.removeGraph);
  const focusGraph = useWorkspaceStore((state) => state.focusGraph);
  const { ref, width } = useElementSize<HTMLDivElement>();
  const [data, setData] = useState<RenderWindowResult | null>(null);
  const [fixedStats, setFixedStats] = useState<RangeStatistics[]>([]);
  const [loading, setLoading] = useState(false);
  const channelById = useMemo(() => new Map(channels.map((channel) => [channel.id, channel])), [channels]);
  const enabledSlots = graph.slots.filter((slot) => slot.channelId);
  const enabledIds = enabledSlots.map((slot) => slot.channelId!);
  const assignedIds = new Set(graph.slots.map((slot) => slot.channelId).filter(Boolean) as string[]);

  const chartChannels = useMemo<ChartChannel[]>(
    () =>
      enabledSlots.flatMap((slot) => {
        const channel = slot.channelId ? channelById.get(slot.channelId) : undefined;
        return channel
          ? [{ id: channel.id, label: channel.displayName, unit: channel.unit, color: slot.color }]
          : [];
      }),
    [channelById, enabledSlots],
  );

  useEffect(() => {
    if (!enabledIds.length || width < 100) {
      setData(null);
      return;
    }
    let active = true;
    setLoading(true);
    void datalogClient.renderWindow(enabledIds, visibleTimeRange, width).then((result) => {
      if (active) {
        setData(result);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [enabledIds.join('|'), visibleTimeRange.end, visibleTimeRange.start, width]);

  useEffect(() => {
    if (!enabledIds.length) {
      setFixedStats([]);
      return;
    }
    let active = true;
    void datalogClient
      .rangeStatistics({ start: session.startTime, end: session.endTime }, enabledIds)
      .then((result) => {
        if (active) setFixedStats(result);
      });
    return () => {
      active = false;
    };
  }, [enabledIds.join('|'), session.endTime, session.startTime]);

  const addDroppedChannel = (channelId: string, slotIndex?: number) => {
    if (!channelById.has(channelId)) return;
    if (assignedIds.has(channelId) && graph.slots[slotIndex ?? -1]?.channelId !== channelId) {
      onNotice('Channel is already in this graph.');
      return;
    }
    if (slotIndex === undefined && graph.slots.every((slot) => slot.channelId)) {
      onNotice('Graph already has four channels. Drop onto a specific slot to replace it.');
      return;
    }
    assignChannel(graph.id, channelId, slotIndex);
  };

  const zoom = (factor: number) => {
    const middle = (visibleTimeRange.start + visibleTimeRange.end) / 2;
    const half = ((visibleTimeRange.end - visibleTimeRange.start) * factor) / 2;
    setVisibleTimeRange({ start: middle - half, end: middle + half });
  };

  return (
    <article
      className={`graph-panel ${focusedGraphId === graph.id ? 'is-focused' : ''}`}
      onPointerDown={(event) => {
        // Do not update the graph card between pointer-down and click on a control.
        // Mobile Safari can cancel the eventual click when that update replaces
        // part of the active target tree.
        if (event.target instanceof Element && event.target.closest('button, input, [role="option"]')) return;
        if (focusedGraphId !== graph.id) focusGraph(graph.id);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
      }}
      onDrop={(event) => {
        event.preventDefault();
        addDroppedChannel(event.dataTransfer.getData('application/x-idash-channel'));
      }}
    >
      <div className="graph-control-strip">
        <div className="graph-config">
          <GraphIdentityBadge order={graph.order} variant="panel" />
          <div className="graph-slots">
            {graph.slots.map((slot) => (
              <div
                key={slot.slotIndex}
                className={`graph-slot ${slot.channelId ? 'has-channel' : ''}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  addDroppedChannel(event.dataTransfer.getData('application/x-idash-channel'), slot.slotIndex);
                }}
              >
                {slot.channelId && <span className="slot-color" style={{ background: slot.color }} aria-hidden="true" />}
                <ChannelCombo
                  channels={channels}
                  selectedId={slot.channelId}
                  unavailableIds={assignedIds}
                  label={`Channel slot ${slot.slotIndex + 1}`}
                  onChange={(channelId) => assignChannel(graph.id, channelId, slot.slotIndex)}
                />
                {slot.channelId && (
                  <button
                    className="slot-remove-button"
                    type="button"
                    onClick={() => assignChannel(graph.id, null, slot.slotIndex)}
                    aria-label={`Remove ${channelById.get(slot.channelId)?.displayName ?? 'channel'} from G${graph.order + 1}`}
                  >
                    <X size={13} aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="graph-toolbar" aria-label="Graph controls">
          <label className="autoscale-control">
            <input
              type="checkbox"
              checked={graph.autoScale}
              onChange={(event) => setAutoScale(graph.id, event.currentTarget.checked)}
            />
            Auto scale
          </label>
          <button className="icon-button" onClick={() => zoom(0.8)} aria-label="Zoom in">
            <Plus size={17} />
          </button>
          <button className="icon-button" onClick={() => zoom(1.25)} aria-label="Zoom out">
            <Minus size={17} />
          </button>
          <button
            className="icon-button"
            onClick={() => setVisibleTimeRange({ start: session.startTime, end: session.endTime })}
            aria-label="Reset time view"
          >
            <RotateCcw size={16} />
          </button>
          <button className="icon-button danger-hover" onClick={() => removeGraph(graph.id)} aria-label="Remove graph">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      <div ref={ref} className="graph-plot-area">
        {!enabledIds.length ? (
          <div className="empty-graph">
            <span>+</span>
            <strong>Select a channel or drag one here</strong>
            <small>Each graph can show up to four channels.</small>
          </div>
        ) : (
          <>
            {loading && <div className="plot-loading">Updating…</div>}
            <TimeSeriesChart
              data={data}
              channels={chartChannels}
              width={width}
              visibleRange={visibleTimeRange}
              sessionRange={{ start: session.startTime, end: session.endTime }}
              cursorTime={cursorTime}
              autoScale={graph.autoScale}
              fixedStats={fixedStats}
              onVisibleRangeChange={setVisibleTimeRange}
              onCursorChange={(time) => setCursorTime(datalogClient.nearestTime(time))}
              onCursorStep={(direction) => {
                const origin = cursorTime ?? visibleTimeRange.start;
                setCursorTime(datalogClient.stepTime(origin, direction));
              }}
            />
          </>
        )}
      </div>
    </article>
  );
}
